import React, { useState, useEffect } from "react";
import {
  getAuth,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from "firebase/auth";
import { getFirestore, doc, getDoc, updateDoc, setDoc } from "firebase/firestore";
import Loading from './Loading.js';
import './Account.css';

const Account = () => {
  const auth = getAuth();
  const db = getFirestore();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [role, setRole] = useState("user"); 
  const [isNameEditable, setIsNameEditable] = useState(true);
  const [successMessage, setSuccessMessage] = useState(""); 

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        setEmail(user.email);

        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setFullName(userData.fullName || "");
            setPhoneNumber(userData.phoneNumber || "");
            setCountry(userData.country || "");
            setState(userData.state || "");
            setCity(userData.city || "");
            setAddress(userData.address || "");
            setRole(userData.role || "user"); 
            if (userData.fullName) {
              setIsNameEditable(false);
            }
          }
        } catch (error) {
          console.error("Error fetching user data: ", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [auth, db]);

  const handleResetPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, user.email);
      setSuccessMessage("Se ha enviado un enlace para restablecer tu contraseña a tu correo.");
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error enviando el correo de restablecimiento:", error);
      alert("Hubo un problema al enviar el correo de restablecimiento.");
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        phoneNumber,
        country,
        state,
        city,
        address,
        role,
      });

      if (isNameEditable) {
        await setDoc(userRef, { fullName }, { merge: true });
        setIsNameEditable(false);
      }

      setSuccessMessage("Datos actualizados con éxito.");
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      console.error("Error updating profile: ", error);
      alert("Hubo un error al actualizar el perfil.");
    }
  };

  if (loading) {
    return (
      <div id="carga" className="containerLoading">
        <Loading />
      </div>
    );
  }

  if (!user) {
    return <div>No has iniciado sesión. Por favor, inicia sesión para ver tu cuenta.</div>;
  }

  return (
    <div className="account-page">
      <h2>Mi Cuenta</h2>
      <form onSubmit={handleUpdateProfile} className="account-form">
        <div className="form-group">
          <label>Correo electrónico</label>
          <input type="email" value={email} disabled />
          <small>El correo electrónico no puede ser modificado.</small>
        </div>

        <div className="form-group">
          <label>Contraseña</label>
          <button 
            type="button" 
            className="btn" 
            onClick={handleResetPassword}
          >
            Restablecer contraseña
          </button>
        </div>

        <div className="form-group">
          <label>Nombre Completo</label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            disabled={!isNameEditable}
            required
          />
          {!isNameEditable && <small>No se puede editar una vez guardado.</small>}
        </div>

        <div className="form-group">
          <label>Teléfono</label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>País</label>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Provincia/Estado</label>
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Ciudad</label>
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Dirección</label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        <div className="form-group">
          <label>Seleccioná el tipo de oficina que deseas crear</label>
          <select
            className="form-control"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            required
          >
            <option value="postulante">Postulate a empleos</option>
            <option value="servicios">Ofrecer servicios</option>
            <option value="empresa">Empresa</option>
            <option value="presupuestos">Solicitar presupuestos</option>
          </select>
        </div>

        {successMessage && (
          <div className="success-message">{successMessage}</div>
        )}

        <button type="submit" className="btn-update">Actualizar Perfil</button>
      </form>
    </div>
  );
};

export default Account;
